<template>
  <div v-if="active" aria-hidden="true" class="cursor-spotlight">
    <div ref="glowEl" class="cursor-spotlight__glow" />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, watch } from "vue";

export default defineComponent({
  name: "CursorSpotlight",
  setup() {
    const active = ref(false);
    const glowEl = ref<HTMLDivElement | null>(null);
    const pos = { x: 0, y: 0 };
    const target = { x: 0, y: 0 };
    let rafId = 0;

    function checkActive(): void {
      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      const coarse = window.matchMedia("(pointer: coarse)").matches;
      const isDark = document.documentElement.classList.contains("dark");
      active.value = !reduced && !coarse && isDark;
    }

    function loop(): void {
      const el = glowEl.value;
      if (el) {
        const lerp = 0.12;
        pos.x += (target.x - pos.x) * lerp;
        pos.y += (target.y - pos.y) * lerp;
        el.style.transform = `translate3d(${pos.x - 300}px, ${pos.y - 300}px, 0)`;
      }
      rafId = requestAnimationFrame(loop);
    }

    function onMove(e: MouseEvent): void {
      target.x = e.clientX;
      target.y = e.clientY;
    }

    let observer: MutationObserver | null = null;

    onMounted(() => {
      checkActive();
      observer = new MutationObserver(checkActive);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });
    });

    watch(active, (on) => {
      if (on) {
        window.addEventListener("mousemove", onMove);
        rafId = requestAnimationFrame(loop);
      } else {
        window.removeEventListener("mousemove", onMove);
        cancelAnimationFrame(rafId);
      }
    });

    onUnmounted(() => {
      observer?.disconnect();
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(rafId);
    });

    return { active, glowEl };
  },
});
</script>

<style lang="scss" scoped>
.cursor-spotlight {
  pointer-events: none;
  position: fixed;
  inset: 0;
  z-index: -1;
  isolation: isolate;
}

.cursor-spotlight__glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  opacity: 0.35;
  will-change: transform;
  background: radial-gradient(
    circle at center,
    rgb(255 175 204 / 0.28) 0%,
    transparent 62%
  );
  filter: blur(2px);
}
</style>

<style lang="scss">
html.dark .cursor-spotlight__glow {
  opacity: 0.4;
}
</style>
